import * as React from 'react';
import { AlertTriangle } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { Button } from './button';
import { type ConfirmRisk, confirmActionVariant, confirmPhraseSatisfied } from '@/lib/confirm-risk';

export type { ConfirmRisk } from '@/lib/confirm-risk';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  risk?: ConfirmRisk;
  /** Critical tier: the exact phrase the user must type to enable confirm. */
  confirmPhrase?: string;
  /** In-dialog error — keeps the dialog open and shows the message (async flows). */
  error?: string | null;
  /** Disables confirm while an async action runs. */
  pending?: boolean;
  /** Called when the user confirms. The caller owns the action + closing the dialog. */
  onConfirm: () => void;
}

/**
 * Themed, risk-tiered confirmation dialog. Replaces ad-hoc `window.confirm()` and
 * one-off AlertDialog blocks so every confirmation is on-theme and tiered.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  risk = 'high',
  confirmPhrase,
  error,
  pending = false,
  onConfirm,
}: ConfirmDialogProps): React.JSX.Element {
  const [typed, setTyped] = React.useState('');
  React.useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const needsPhrase = risk === 'critical' && Boolean(confirmPhrase);
  const enabled = !pending && confirmPhraseSatisfied(risk, confirmPhrase, typed);
  const showWarning = risk === 'high' || risk === 'critical';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {showWarning && <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />}
            {title}
          </AlertDialogTitle>
          {description !== undefined && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {needsPhrase && (
          <div className="px-1">
            <label className="text-xs text-text-secondary">
              Type <span className="font-mono text-text-primary">{confirmPhrase}</span> to confirm
            </label>
            <input
              value={typed}
              onChange={e => setTyped(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-surface-inset px-2 py-1 text-sm text-text-primary"
              aria-label={`Type ${confirmPhrase ?? ''} to confirm`}
            />
          </div>
        )}

        {error != null && error !== '' && <p className="px-1 text-sm text-error">{error}</p>}

        <AlertDialogFooter>
          <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
          <Button variant={confirmActionVariant(risk)} disabled={!enabled} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Options for an imperative confirmation (everything except the controlled open state). */
export type ConfirmOptions = Omit<
  ConfirmDialogProps,
  'open' | 'onOpenChange' | 'onConfirm' | 'error' | 'pending'
>;

/**
 * Imperative confirmation: `const ok = await confirm({ ... })`. Render
 * `confirmDialog` once in the component. Resolves true on confirm, false on
 * cancel/dismiss. Ideal for replacing `if (window.confirm(...))` handlers that
 * fire a synchronous action.
 */
export function useConfirm(): {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  confirmDialog: React.ReactNode;
} {
  const [options, setOptions] = React.useState<ConfirmOptions | null>(null);
  const resolverRef = React.useRef<((value: boolean) => void) | null>(null);

  const confirm = React.useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>(resolve => {
        // Defensive: if a confirm is somehow already pending (re-entrancy),
        // settle the previous one false so its awaiter never hangs.
        resolverRef.current?.(false);
        resolverRef.current = resolve;
        setOptions(opts);
      }),
    []
  );

  const settle = React.useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOptions(null);
  }, []);

  const confirmDialog =
    options !== null ? (
      <ConfirmDialog
        {...options}
        open
        onOpenChange={next => {
          if (!next) settle(false);
        }}
        onConfirm={() => settle(true)}
      />
    ) : null;

  return { confirm, confirmDialog };
}
